import type { DashboardRange } from "@adpilot/shared";
import { create } from "zustand";
import {
  addReportingDays,
  DEFAULT_REPORTING_TIME_ZONE,
  type ReportingTimeZone,
  reportingDateInTimeZone,
} from "@/lib/reporting-time-zone";

export const REPORTING_PERIOD_PRESETS = ["yesterday", "7d", "14d", "30d"] as const;

/** The dashboard date range picker's preset rail (Paper "Date Range Picker"). */
export const TOOLBAR_PERIOD_PRESETS = [
  "today",
  "yesterday",
  "7d",
  "30d",
  "90d",
  "this_month",
  "last_month",
  "this_quarter",
  "ytd",
] as const;

export type ReportingPeriodPreset =
  | (typeof REPORTING_PERIOD_PRESETS)[number]
  | (typeof TOOLBAR_PERIOD_PRESETS)[number];

// The APIs resolve these names themselves; every other preset travels as explicit dates.
const API_RANGES = new Set<string>(["today", "yesterday", "7d", "14d", "30d"]);

export type ReportingPeriod =
  | { type: "preset"; value: ReportingPeriodPreset }
  | { type: "custom"; startDate: string; endDate: string };

export interface ResolvedReportingPeriod {
  startDate: string;
  endDate: string;
}

export function resolveReportingPeriod(
  period: ReportingPeriod,
  now = new Date(),
  timeZone: ReportingTimeZone = DEFAULT_REPORTING_TIME_ZONE,
): ResolvedReportingPeriod {
  if (period.type === "custom") {
    return { startDate: period.startDate, endDate: period.endDate };
  }

  const today = reportingDateInTimeZone(now, timeZone);
  const [year = "", month = ""] = today.split("-");
  switch (period.value) {
    case "today":
      return { startDate: today, endDate: today };
    case "yesterday": {
      const yesterday = addReportingDays(today, -1);
      return { startDate: yesterday, endDate: yesterday };
    }
    case "this_month":
      return { startDate: `${year}-${month}-01`, endDate: today };
    case "last_month": {
      const lastDay = addReportingDays(`${year}-${month}-01`, -1);
      return { startDate: `${lastDay.slice(0, 7)}-01`, endDate: lastDay };
    }
    case "this_quarter": {
      const quarterMonth = Math.floor((Number(month) - 1) / 3) * 3 + 1;
      return { startDate: `${year}-${String(quarterMonth).padStart(2, "0")}-01`, endDate: today };
    }
    case "ytd":
      return { startDate: `${year}-01-01`, endDate: today };
    default: {
      const days = Number.parseInt(period.value.replace("d", ""), 10);
      return { startDate: addReportingDays(today, -(days - 1)), endDate: today };
    }
  }
}

export function reportingPeriodSearchParams(
  period: ReportingPeriod,
  timeZone: ReportingTimeZone = DEFAULT_REPORTING_TIME_ZONE,
  now = new Date(),
): {
  range: DashboardRange;
  startDate?: string;
  endDate?: string;
} {
  const resolved = resolveReportingPeriod(period, now, timeZone);
  return {
    range:
      period.type === "preset" && API_RANGES.has(period.value)
        ? (period.value as DashboardRange)
        : "30d",
    startDate: resolved.startDate,
    endDate: resolved.endDate,
  };
}

interface ReportingPeriodState {
  period: ReportingPeriod;
  setPeriod: (period: ReportingPeriod) => void;
}

export const useReportingPeriodStore = create<ReportingPeriodState>((set) => ({
  period: { type: "preset", value: "30d" },
  setPeriod: (period) => set({ period }),
}));
